import { useTranslation } from 'react-i18next'

import { ValidationSubmissionConfigurationResponse } from '@percent/admin-dashboard/api/actions/validationSubmissions/validationSubmissions.types'
import { useQuery, useRequiredParams } from '@percent/admin-dashboard/common/hooks'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { CardDashboard, Flex, Typography } from '@percent/lemonade'
import { ProgramAgentVerificationDetails } from './Sections/ProgramAgentVerificationDetails'
import { ProgramComplianceDetails } from './Sections/ProgramComplianceDetails'
import { ProgramCountryComplianceDetails } from './Sections/ProgramCountryComplianceDetails'
import { ProgramEligibilityDetails } from './Sections/ProgramEligibilityDetails'
import { ProgramFraudCheckDetails } from './Sections/ProgramFraudCheckDetails'
import { ProgramOthers } from './Sections/ProgramOthers'
import { ProgramOutcomeEmails } from './Sections/ProgramOutcomeEmails'
import { ProgramPartnerFields } from './Sections/ProgramPartnerFields'
import { ProgramDetailRow } from './Sections/Shared/ProgramDetailRow'
import { ProgramDetailTable } from './Sections/Shared/ProgramDetailTable'

const renderProgramCheckDetails = (
  check: ValidationSubmissionConfigurationResponse['configuration']['checks'][number]
) => {
  switch (check.type) {
    case 'agent_verification':
      return <ProgramAgentVerificationDetails key={check.type} {...check} />
    case 'compliance':
      return <ProgramComplianceDetails key={check.type} {...check} />
    case 'country_compliance':
      return <ProgramCountryComplianceDetails key={check.type} {...check} />
    case 'eligibility':
      return <ProgramEligibilityDetails key={check.type} {...check} />
    case 'fraud_check':
      return <ProgramFraudCheckDetails key={check.type} {...check} />
    default:
      return null
  }
}

export function ProgramDetail() {
  const { id } = useRequiredParams()
  const { t } = useTranslation()
  const { validationSubmissionsService } = useServices()

  const [{ data, isLoading }] = useQuery(
    validationSubmissionsService.getValidationSubmissionConfiguration,
    { id },
    { skip: !id }
  )

  if (!data) return null

  const disallowedItems = data.configuration.disallowedOrganisations.relationships.map(({ type, relationship }) => {
    const relationshipType = t(`relationshipTypes.${type}`)
    const relationshipStatus = t(`relationship.${relationship}`)

    return `${relationshipType} (${relationshipStatus})`
  })

  return (
    <CardDashboard>
      <Flex gap={16} direction="column">
        <Typography variant="h2">{data.name || t('typography.unnamedProgram')}</Typography>
        <Typography variant="h4">{t('typography.configuration')}</Typography>
        <Flex gap={16} direction="column">
          <ProgramDetailTable
            title={t('program.label.allowedOrganisation')}
            items={data.configuration.allowedOrganisations.types.map(type => t(`organisationTypes.${type}`))}
            status="on"
          />
          <ProgramDetailTable title={t('program.label.disallowedOrganisation')} items={disallowedItems} />
          <ProgramDetailTable
            title={t('program.label.checks')}
            items={data.configuration.checks.map(({ type }) => t(`program.label.checks.${type}`))}
          />
          <ProgramDetailRow
            title={t('program.label.dynamicVsStatus')}
            status={data.configuration.isStatusDynamic ? 'on' : 'off'}
          />
        </Flex>

        {data.configuration.checks.map(renderProgramCheckDetails)}

        <ProgramOutcomeEmails {...data} />

        <ProgramOthers {...data} />

        <ProgramPartnerFields {...data} isLoading={isLoading} />
      </Flex>
    </CardDashboard>
  )
}
